import { Modal } from './Modal.js';
import { resourceLabel } from './schema.js';
import { happiness } from './happiness.js';
import type { Economy } from './Economy.js';
import type { Progression } from './progression.js';
import type { GameConfig, Placement } from '../types.js';

interface HomeWindowOptions {
    config: GameConfig;
    progression: Progression;
    economy: Economy;
}

export class HomeWindow {
    private readonly config: GameConfig;
    private readonly progression: Progression;
    private readonly economy: Economy;
    private readonly modal: Modal;
    private readonly body: HTMLElement;
    private placements: Placement[] = [];

    constructor({ config, progression, economy }: HomeWindowOptions) {
        this.config = config;
        this.progression = progression;
        this.economy = economy;

        this.modal = new Modal({ title: config.gameTitle || 'HOME', variant: 'home' });
        this.body = document.createElement('div');
        this.body.className = 'home-summary';
        this.modal.body.appendChild(this.body);
    }

    open(placements: Placement[]): void {
        this.placements = placements;
        this.render();
        this.modal.open();
    }

    close(): void {
        this.modal.close();
    }

    toggle(placements: Placement[]): void {
        if (this.modal.isOpen) this.close();
        else this.open(placements);
    }

    private render(): void {
        this.body.innerHTML = '';

        if (this.config.setting || this.config.description) {
            const blurb = document.createElement('p');
            blurb.className = 'home-summary__blurb';
            blurb.textContent = this.config.setting || this.config.description || '';
            this.body.appendChild(blurb);
        }

        const stats = document.createElement('div');
        stats.className = 'home-summary__stats';
        stats.appendChild(this.stat('Level', `${this.progression.level}${this.eraSuffix()}`));
        stats.appendChild(this.stat('Happiness', `${happiness(this.config)}%`));
        stats.appendChild(this.stat('Buildings', String(this.placements.length)));
        this.body.appendChild(stats);

        const balances = document.createElement('div');
        balances.className = 'home-summary__balances';
        Object.entries(this.economy.snapshot()).forEach(([id, value]) => {
            balances.appendChild(this.stat(resourceLabel(this.config, id), String(value)));
        });
        this.body.appendChild(balances);
    }

    private eraSuffix(): string {
        const era = this.config.ui?.level?.era;
        return era ? ` · ${era}` : '';
    }

    private stat(label: string, value: string): HTMLElement {
        const el = document.createElement('div');
        el.className = 'home-summary__stat';

        const valueEl = document.createElement('span');
        valueEl.className = 'home-summary__stat-value';
        valueEl.textContent = value;
        el.appendChild(valueEl);

        const labelEl = document.createElement('span');
        labelEl.className = 'home-summary__stat-label';
        labelEl.textContent = label;
        el.appendChild(labelEl);

        return el;
    }
}
